import React from 'react';
import { OverlayTrigger, Tooltip, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaCaretDown, FaCaretRight, FaCheckCircle, FaTimesCircle, FaQuestionCircle } from 'react-icons/fa';

import TrafficLight from '../../common/TrafficLight';
import ActionAreaTrafficLight from '../../common/ActionAreaTrafficLight';
import QuestionTrafficLight from '../../common/QuestionTrafficLight';
import ArrowIcon from '../../../assets/gptw_icons/Icons_rot/arrow-circle-up-right.png';
import {
    addActionAreaCheckpointStatusCount,
    addMeasureCheckpointStatusCount,
    createActionAreaCheckpointCounts,
    createMeasureCheckpointCounts,
} from '../../../utils/checkpointStatus';

const ExpandableDashboardRow = ({ row, toggleOpen, isOpen, tooltipContainerRef, hasChildren, level, sortedCheckpoints }) => {
    // Use sortedCheckpoints (date-ordered) or fall back to grouped checkpoints for backwards compatibility
    const allCheckpoints = sortedCheckpoints || [];

    // Get status color based on status value
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'ja':
            case 'erreicht':
                return 'var(--status-positive)';
            case 'mehr als erreicht':
                return 'var(--status-overachieved)';
            case 'nein':
            case 'verzögert':
            case 'nicht erreicht':
                return 'var(--status-negative)';
            case 'gestoppt':
            case 'schwierigkeiten':
                return 'var(--status-stopped)';
            case 'teilweise':
            case 'planmäßig':
            case 'teilweise erreicht':
                return 'var(--status-on-track)';
            default:
                return 'var(--status-no-status)';
        }
    };

    // Helper function to get tooltip text for a status
    const getStatusTooltipText = (status) => {
        if (!status) return "Keine Angabe";

        const statusDesc = status.status_description || "";
        const baseText = status.status || "Keine Angabe";

        return statusDesc ? `${baseText}: ${statusDesc}` : baseText;
    };

    // Find unit-specific question status in checkpoint
    const findQuestionStatus = (checkpoint) => {
        if (!checkpoint || !checkpoint.statuses) return null;
    
        // For each status in this checkpoint
        const matchingStatus = checkpoint.statuses.find(status => {
            // Check if this is a question status (has question property)
            const isQuestionStatus = status.question && !status.measure && !status.actionarea;
    
            // Check if this status belongs to this unit
            const unitMatches = status.unit
                ? String(status.unit) === String(row.id) || String(status.unit.id) === String(row.id)
                : false;
    
            return isQuestionStatus && unitMatches;
        });
    
        return matchingStatus;
    };

    // Render status for question checkpoint
    const renderQuestionStatus = (checkpoint) => {
        // If this is a parent unit with children, show question traffic light
        if (hasChildren && row.questionResponses) {
            const checkpointResponses = row.questionResponses[checkpoint.id];
    
            if (!checkpointResponses) {
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Keine Daten für untergeordnete Einheiten gefunden</Tooltip>}
                    >
                        <div className="text-center">
                            <FaQuestionCircle color="var(--status-no-status)" size={24} />
                            <div className="small text-muted mt-1">0%</div>
                        </div>
                    </OverlayTrigger>
                );
            }
    
            // Ensure we have valid numbers for all counts
            const yesCount = checkpointResponses.yesCount || 0;
            const noCount = checkpointResponses.noCount || 0;
            const partialCount = checkpointResponses.partialCount || 0;
            const unknownCount = checkpointResponses.unknownCount || 0;
            const total = checkpointResponses.total || 0;
            
            // Prevent division by zero
            if (total === 0) {
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Keine Antworten vorhanden</Tooltip>}
                    >
                        <div className="text-center">
                            <FaQuestionCircle color="var(--status-no-status)" size={24} />
                            <div className="small text-muted mt-1">0%</div>
                        </div>
                    </OverlayTrigger>
                );
            }
    
            // Create data object for the traffic light component
            const data = {
                unknown: Math.round((unknownCount / total) * 100),
                no: Math.round((noCount / total) * 100),
                yes: Math.round((yesCount / total) * 100),
                partial: Math.round((partialCount / total) * 100),
                total: total,
                unknownCount,
                noCount,
                yesCount,
                partialCount
            };
    
            return <QuestionTrafficLight data={data} tooltipContainerRef={tooltipContainerRef} />;
        }
    
        // For leaf units, show the existing status indicators
        const status = findQuestionStatus(checkpoint);
    
        if (!status) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Keine Antwort für diese Einheit</Tooltip>}
                >
                    <div className="text-center">
                        <FaQuestionCircle color="var(--status-no-status)" size={24} />
                    </div>
                </OverlayTrigger>
            );
        }

        // Determine what to render based on status value
        switch (status.status?.toLowerCase()) {
            case 'ja':
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{getStatusTooltipText(status)}</Tooltip>}
                    >
                        <div>
                            <FaCheckCircle color={getStatusColor(status.status)} size={24} />
                            {status.communication_date && (
                                <div className="small text-muted mt-1">
                                    {new Date(status.communication_date).toLocaleDateString('de-DE')}
                                </div>
                            )}
                        </div>
                    </OverlayTrigger>
                );
            case 'nein':
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{getStatusTooltipText(status)}</Tooltip>}
                    >
                        <div>
                            <FaTimesCircle color={getStatusColor(status.status)} size={24} />
                            {status.communication_date && (
                                <div className="small text-muted mt-1">
                                    {new Date(status.communication_date).toLocaleDateString('de-DE')}
                                </div>
                            )}
                        </div>
                    </OverlayTrigger>
                );
            case 'teilweise':
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{getStatusTooltipText(status)}</Tooltip>}
                    >
                        <div>
                            <Badge bg="warning" pill style={{ fontSize: '14px', padding: '8px' }}>
                                Teilweise
                            </Badge>
                            {status.communication_date && (
                                <div className="small text-muted mt-1">
                                    {new Date(status.communication_date).toLocaleDateString('de-DE')}
                                </div>
                            )}
                        </div>
                    </OverlayTrigger>
                );
            default:
                // For any other status value, show the question mark icon instead of a badge
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{getStatusTooltipText(status)}</Tooltip>}
                    >
                        <div className="text-center">
                            <FaQuestionCircle
                                color={getStatusColor(status.status)}
                                size={24}
                            />
                            {status.communication_date && (
                                <div className="small text-muted mt-1">
                                    {new Date(status.communication_date).toLocaleDateString('de-DE')}
                                </div>
                            )}
                        </div>
                    </OverlayTrigger>
                );
        }
    };

    // Find unit-specific measure statuses for a checkpoint 
    // eslint-disable-next-line no-unused-vars
    const findMeasureStatusLegacy = (checkpoint) => {
        // For actual leaf units (not aggregated parents)
        if (!hasChildren) {
            // First, check if this checkpoint has measure statuses for this unit
            const measureStatuses = checkpoint.statuses?.filter(status =>
                (String(status.unit?.id) === String(row.id) || String(status.unit) === String(row.id))
            );
        
            if (measureStatuses && measureStatuses.length > 0) {
                // Count statuses by type from the checkpoint data
                let stoppedCount = 0;
                let notStartedCount = 0;
                let delayedProgressCount = 0;
                let onTrackProgressCount = 0;
                let completedCount = 0;
        
                measureStatuses.forEach(status => {
                    const statusValue = status.status?.toLowerCase();
                    
                    switch (statusValue) {
                        case 'gestoppt':
                            stoppedCount++;
                            break;
                        case 'nicht begonnen':
                            notStartedCount++;
                            break;
                        case 'in bearbeitung - verzögert':
                            delayedProgressCount++;
                            break;
                        case 'in bearbeitung - nach plan':
                            onTrackProgressCount++;
                            break;
                        case 'abgeschlossen':
                            completedCount++;
                            break;
                        // Legacy status handling
                        case 'planmäßig':
                            onTrackProgressCount++;
                            break;
                        case 'verzögert':
                            delayedProgressCount++;
                            break;
                        case 'schwierigkeiten':
                            stoppedCount++;
                            break;
                        default:
                            notStartedCount++;
                            break;
                    }
                });
        
                const total = stoppedCount + notStartedCount + delayedProgressCount + 
                              onTrackProgressCount + completedCount;
                
                if (total > 0) {
                    return {
                        stopped: Math.floor((stoppedCount / total) * 100),
                        notStarted: Math.floor((notStartedCount / total) * 100),
                        delayedProgress: Math.floor((delayedProgressCount / total) * 100),
                        onTrackProgress: Math.floor((onTrackProgressCount / total) * 100),
                        completed: Math.floor((completedCount / total) * 100),
                        measures: total
                    };
                }
            }
        }
    
        // For parent units, check if we have checkpoint-specific data available
        if (row.checkpointMeasureData && row.checkpointMeasureData[checkpoint.id]) {
            return row.checkpointMeasureData[checkpoint.id];
        }
        
        // For parent units with measure checkpoint responses, use the aggregated data
        if (row.measureCheckpointResponses && row.measureCheckpointResponses[checkpoint.id]) {
            const data = row.measureCheckpointResponses[checkpoint.id];
            
            // Calculate percentages
            const total = data.stoppedCount + data.notStartedCount + data.delayedProgressCount + 
                          data.onTrackProgressCount + data.completedCount;
                          
            if (total > 0) {
                return {
                    stopped: Math.floor((data.stoppedCount / total) * 100),
                    notStarted: Math.floor((data.notStartedCount / total) * 100),
                    delayedProgress: Math.floor((data.delayedProgressCount / total) * 100),
                    onTrackProgress: Math.floor((data.onTrackProgressCount / total) * 100),
                    completed: Math.floor((data.completedCount / total) * 100),
                    measures: total
                };
            }
        }
        
        // Default empty state if no data found
        return {
            stopped: 0,
            notStarted: 0,
            delayedProgress: 0,
            onTrackProgress: 0,
            completed: 0,
            measures: 0
        };
    };

    // Find unit-specific action area statuses for a checkpoint
    // eslint-disable-next-line no-unused-vars
    const findActionAreaStatusLegacy = (checkpoint) => {
        // For actual units (not aggregated parents)
        if (!hasChildren) {
            // First, check if this checkpoint has any action area statuses for this unit
            const actionAreaStatuses = checkpoint.statuses?.filter(status =>
                status.actionarea &&
                (String(status.unit?.id) === String(row.id) || String(status.unit) === String(row.id))
            );

            if (actionAreaStatuses && actionAreaStatuses.length > 0) {
                // Count statuses by type from the checkpoint data
                return actionAreaStatuses.reduce((counts, status) => {
                    const statusValue = status.status?.toLowerCase() || '';

                    if (statusValue === 'mehr als erreicht') {
                        counts.achieved += 1;
                    } else if (statusValue === 'erreicht') {
                        counts.partiallyAchieved += 1;
                    } else {
                        // Empty, null, 'nicht erreicht', or any other value = not achieved
                        counts.notAchieved += 1;
                    }

                    counts.total += 1;
                    return counts;
                }, {
                    notAchieved: 0,
                    partiallyAchieved: 0,
                    achieved: 0,
                    total: 0
                });
            }

            // If no checkpoint statuses found, use the API response data
            if (row.actionAreas > 0) {
                const achieved = row.finishedActionAreas || 0;
                const notAchieved = row.actionAreas - achieved;

                // This is a fallback that assumes finished action areas are "mehr als erreicht"
                // If you have better data about which are just "erreicht" vs "mehr als erreicht"
                // you could improve this logic
                return {
                    achieved: achieved, // Using "achieved" to represent "mehr als erreicht"
                    partiallyAchieved: 0, // We don't know which are just "erreicht" from the API data
                    notAchieved: notAchieved,
                    total: row.actionAreas
                };
            }

            // Default empty state
            return {
                notAchieved: 0,
                partiallyAchieved: 0,
                achieved: 0,
                total: 0
            };
        }

        // For parent units with children, use the aggregated action area responses
        if (row.actionAreaResponses && row.actionAreaResponses[checkpoint.id]) {
            return row.actionAreaResponses[checkpoint.id];
        }

        // Fallback if no data is available
        return {
            notAchieved: 0,
            partiallyAchieved: 0,
            achieved: 0,
            total: 0
        };
    };

    const toMeasurePercentages = (counts) => {
        const total = counts.noStatusCount + counts.stoppedCount + counts.notStartedCount +
            counts.delayedProgressCount + counts.onTrackProgressCount + counts.completedCount;

        if (total === 0) {
            return {
                noStatus: 0,
                stopped: 0,
                notStarted: 0,
                delayedProgress: 0,
                onTrackProgress: 0,
                completed: 0,
                measures: 0
            };
        }

        return {
            noStatus: Math.floor((counts.noStatusCount / total) * 100),
            stopped: Math.floor((counts.stoppedCount / total) * 100),
            notStarted: Math.floor((counts.notStartedCount / total) * 100),
            delayedProgress: Math.floor((counts.delayedProgressCount / total) * 100),
            onTrackProgress: Math.floor((counts.onTrackProgressCount / total) * 100),
            completed: Math.floor((counts.completedCount / total) * 100),
            measures: total
        };
    };

    const findMeasureStatus = (checkpoint) => {
        if (!hasChildren) {
            const measureStatuses = checkpoint.statuses?.filter(status =>
                String(status.unit?.id) === String(row.id) || String(status.unit) === String(row.id)
            );

            if (measureStatuses && measureStatuses.length > 0) {
                const counts = createMeasureCheckpointCounts();
                measureStatuses.forEach(status => {
                    addMeasureCheckpointStatusCount(counts, status.status);
                });
                return toMeasurePercentages(counts);
            }
        }

        if (row.checkpointMeasureData && row.checkpointMeasureData[checkpoint.id]) {
            return row.checkpointMeasureData[checkpoint.id];
        }

        if (row.measureCheckpointResponses && row.measureCheckpointResponses[checkpoint.id]) {
            return toMeasurePercentages(row.measureCheckpointResponses[checkpoint.id]);
        }

        return {
            noStatus: 0,
            stopped: 0,
            notStarted: 0,
            delayedProgress: 0,
            onTrackProgress: 0,
            completed: 0,
            measures: 0
        };
    };

    const findActionAreaStatus = (checkpoint) => {
        if (!hasChildren) {
            const actionAreaStatuses = checkpoint.statuses?.filter(status =>
                status.actionarea &&
                (String(status.unit?.id) === String(row.id) || String(status.unit) === String(row.id))
            );

            if (actionAreaStatuses && actionAreaStatuses.length > 0) {
                const counts = createActionAreaCheckpointCounts();
                actionAreaStatuses.forEach(status => {
                    addActionAreaCheckpointStatusCount(counts, status.status);
                });
                return counts;
            }

            if (row.actionAreas > 0) {
                return {
                    noStatus: row.actionAreas,
                    notAchieved: 0,
                    partiallyAchieved: 0,
                    achieved: 0,
                    total: row.actionAreas
                };
            }

            return createActionAreaCheckpointCounts();
        }

        if (row.actionAreaResponses && row.actionAreaResponses[checkpoint.id]) {
            return row.actionAreaResponses[checkpoint.id];
        }

        return createActionAreaCheckpointCounts();
    };

    return (
        <tr>
            <td
                className="align-middle text-nowrap position-sticky bg-white"
                style={{
                    left: 0,
                    zIndex: 10,
                    boxShadow: '5px 0 5px -5px rgba(0,0,0,.1)'
                }}
            >
                <div className="d-flex align-items-center">
                    <div
                        onClick={hasChildren ? toggleOpen : null}
                        className={`d-flex align-items-center text-nowrap cursor-pointer ${hasChildren ? 'pointer' : ''}`}
                        style={{ paddingLeft: `${level * 20}px` }}
                    >
                        {hasChildren && (
                            isOpen ? <FaCaretDown className="me-2" /> : <FaCaretRight className="me-2" />
                        )}
                        {row.unitname}
                    </div>
                    {row.visibility_level !== 0 && (
                        <div className="ms-auto">
                            <OverlayTrigger
                                key={`top-${row.id}`}
                                placement='top'
                                overlay={
                                    <Tooltip id={`tooltip-top-${row.id}`}>
                                        zum Actionboard
                                    </Tooltip>
                                }
                            >
                                <Link to={`/actionboard?unitId=${row.id}`}>
                                    <Button variant="link" className="p-0 ms-2">
                                        <img src={ArrowIcon} alt="Arrow Icon" className="action-icon" />
                                    </Button>
                                </Link>
                            </OverlayTrigger>
                        </div>
                    )}
                </div>
            </td>



            {/* All checkpoint columns - ordered by date */}
        {allCheckpoints.map((checkpoint) => {
            const checkpointType = checkpoint.checkpoint_type || 'measures';
            
            // Render based on checkpoint type
            if (checkpointType === 'questions') {
                return (
                    <td
                        key={`question-${checkpoint.id}`}
                        className="text-center align-middle question-status-cell"
                        style={{ width: '200px', maxWidth: '200px' }}
                    >
                        {renderQuestionStatus(checkpoint)}
                    </td>
                );
            } else if (checkpointType === 'actionareas') {
                const actionAreaData = findActionAreaStatus(checkpoint);
                return (
                    <td
                        key={`actionarea-${checkpoint.id}`}
                        className="text-center align-middle text-nowrap actionarea-column"
                        style={{ width: '200px', maxWidth: '200px' }}
                    >
                        <ActionAreaTrafficLight
                            data={actionAreaData}
                            tooltipContainerRef={tooltipContainerRef}
                        />
                    </td>
                );
            } else {
                // Default to measures
                const checkpointData = findMeasureStatus(checkpoint);
                return (
                    <td
                        key={`measure-${checkpoint.id}`}
                        className="text-center align-middle text-nowrap ampelf-column"
                        style={{ width: '200px', maxWidth: '200px' }}
                    >
                        <TrafficLight row={checkpointData} tooltipContainerRef={tooltipContainerRef} />
                    </td>
                );
            }
        })}
    </tr>
);
};

export default ExpandableDashboardRow;
